import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import IPDevBannerImage from "../../../../assets/services/asic-design/asicDesign2.png";
import ProfileImage from "../../../../assets/blogpages/person.png";
import "./IPDevelopment.css";
import { AnimatePresence, motion } from "framer-motion";

// Simple highlight component: wraps matches in <mark>
const Highlight = ({ text = "", query = "" }) => {
  if (!query) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <mark key={i}>{part}</mark>
    ) : (
      part
    )
  );
};

const IPDevelopment = () => {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const closeOverlay = () => setShowPopup(false);

  // toggle reply‐forms per comment
  const [replyVisible, setReplyVisible] = useState({});
  const toggleReply = (id) =>
    setReplyVisible((prev) => ({ ...prev, [id]: !prev[id] }));

  // refs for scroll‐to‐section
  const sectionRefs = {
    intro: useRef(null),
    overview: useRef(null),
    integration: useRef(null),
    customization: useRef(null),
    verification: useRef(null),
    performance: useRef(null),
    security: useRef(null),
    future: useRef(null),
    comments: useRef(null),
  };

  const scrollTo = (ref) =>
    ref.current && ref.current.scrollIntoView({ behavior: "smooth", block: "center" });

  const handleSearch = () => setSearchTerm(searchInput.trim());
  useEffect(() => {
    if (!searchTerm) return;
    const firstMark = document.querySelector("mark");
    firstMark?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [searchTerm]);

  // fade‐in/out on scroll
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) =>
          e.isIntersecting
            ? e.target.classList.add("visible")
            : e.target.classList.remove("visible")
        ),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".fade-section").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // auto-dismiss popup
  useEffect(() => {
    if (showPopup) {
      const t = setTimeout(() => setShowPopup(false), 2000);
      return () => clearTimeout(t);
    }
  }, [showPopup]);

  const handleReplySubmit = (e) => {
    e.preventDefault();
    setShowPopup(true);
    e.target.reset();
  };
  const handleCommentSubmit = (e) => {
    e.preventDefault();
    setShowPopup(true);
    e.target.reset();
  };

  return (
    <div className="blog-post-page ip-development">
      {/* Banner */}
      <div className="blog-banner fade-section">
        <div className="banner-overlay">
          <h1>IP Development</h1>
          <div className="breadcrumb">
            <Link to="/services/asic-design">Services</Link> | IP Development
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      <div className="mobile-nav fade-section">
        <div className="search-box">
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            type="text"
            placeholder="Search keyword..."
            className="search-input"
          />
          <button onClick={handleSearch} className="search-button">
            Search
          </button>
        </div>
        <div className="table-of-contents">
          <h4>In This Article</h4>
          <ul>
            {Object.entries(sectionRefs).map(([key, ref]) => (
              <li key={key}>
                <button onClick={() => scrollTo(ref)}>
                  {{
                    intro: "Introduction",
                    overview: "IP Overview & Classification",
                    integration: "Integration Flow",
                    customization: "Customization & Parameterization",
                    verification: "Verification & Validation",
                    performance: "Performance & QoR",
                    security: "Security & Licensing",
                    future: "Future Directions",
                    comments: "Comments",
                  }[key]}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="blog-content-container">
        {/* Left Column */}
        <div className="blog-left-column">
          <img
            className="post-main-image fade-section"
            src={IPDevBannerImage}
            alt="IP Development"
          />

          <div className="post-info-row fade-section">
            <span className="post-author">By John Doe</span>
            <span className="post-category">Category: IP Design</span>
            <button
              className="comment-link"
              onClick={() => scrollTo(sectionRefs.comments)}
            >
              2 Comments
            </button>
          </div>

          {/* 1. Introduction */}
          <h2 ref={sectionRefs.intro} className="fade-section">
            Introduction to IP Development
          </h2>
          <p className="fade-section">
            <Highlight
              text="Intellectual Property (IP) in semiconductor design refers to reusable functional blocks—ranging from simple interfaces to complex CPU cores—that are licensed, integrated, and customized to accelerate SoC development."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="IP development enables chip architects to assemble proven building blocks, reducing non-recurring engineering costs, minimizing time-to-market, and leveraging best-in-class implementations for common functions like memory controllers, SERDES, or cryptographic accelerators."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="A robust IP strategy includes standardized delivery formats, thorough documentation, verification artifacts, and configuration tools—empowering design teams to focus on differentiation rather than reinventing well-established functions."
              query={searchTerm}
            />
          </p>

          {/* 2. Overview & Classification */}
          <h2 ref={sectionRefs.overview} className="fade-section">
            IP Overview & Classification
          </h2>
          <ul className="fade-section">
            {[
              "Soft IP (RTL source code)",
              "Firm IP (synthesizable netlist + abstracts)",
              "Hard IP (GDSII/macros at target technology)",
              "Verification IP (VIP: protocol checkers, monitors)",
              "Analog/Mixed-Signal IP blocks",
            ].map((item) => (
              <li key={item}>
                <Highlight text={item} query={searchTerm} />
              </li>
            ))}
          </ul>
          <p className="fade-section">
            <Highlight
              text="Soft IP provides ultimate flexibility—RTL can be retargeted to new process nodes—but requires integration and timing closure effort. Firm IP balances portability with faster integration, whereas Hard IP delivers the highest QoR at the cost of process-specific inflexibility."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Verification IP accelerates protocol compliance testing by providing a comprehensive suite of testbenches, constrained-random stimulus, and coverage models, ensuring that both the IP and the top-level interconnect adhere to standards like AXI, PCIe, or USB."
              query={searchTerm}
            />
          </p>

          {/* 3. Integration Flow */}
          <h2 ref={sectionRefs.integration} className="fade-section">
            Integration Flow
          </h2>
          <p className="fade-section">
            <Highlight
              text="Integrating IP into an SoC requires wrapper generation, port remapping, clock/reset domain alignment, and bus/AXI interconnect configuration. Tools like IP-XACT automate packaging, presenting a machine-readable manifest of ports, parameters, and dependencies."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="A typical flow begins with importing the IP package into the project, configuring parameters (data width, FIFO depth, protocol modes), and generating a Vivado/Quartus block wrapper or scripts to instantiate the IP in a top-level RTL file."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Subsequent steps include static connectivity checks, lint runs to enforce naming conventions, and incremental synthesis + timing analysis to catch integration mismatches early—before full-chip implementation."
              query={searchTerm}
            />
          </p>

          {/* 4. Customization & Parameterization */}
          <h2 ref={sectionRefs.customization} className="fade-section">
            Customization & Parameterization
          </h2>
          <p className="fade-section">
            <Highlight
              text="Most IP blocks expose parameters—FIFO depths, cache sizes, encryption key widths—that tailor functionality and resource trade-offs. A well-designed IP offers clear parameter definitions, valid-range assertions, and automated configuration UIs."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Automation scripts or GUI tools often guide users through parameter sweeps, generating multiple configurations—each with its own constraints file and UVM configuration—to benchmark performance, area, and power for the desired use case."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="IP-XACT and vendor-specific metadata formats enable tool-agnostic parameter management, ensuring a single source of truth for IP configuration across simulation, synthesis, and physical implementation."
              query={searchTerm}
            />
          </p>

          {/* 5. Verification & Validation */}
          <h2 ref={sectionRefs.verification} className="fade-section">
            Verification & Validation
          </h2>
          <p className="fade-section">
            <Highlight
              text="IP verification goes beyond functional tests—it includes corner-case stress tests, protocol compliance suites, and performance benchmarks. UVM environments with scoreboard checks verify data integrity end-to-end."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Formal verification engines can mathematically prove key properties—deadlock freedom in NoC routers or bit-true correctness of encoders—providing a higher assurance level than simulation alone."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Post-silicon validation uses built-in self-test (BIST) hooks and boundary scan to ensure the integrated IP functions correctly in the live hardware environment, capturing silicon-specific corner cases."
              query={searchTerm}
            />
          </p>

          {/* 6. Performance & QoR Tuning */}
          <h2 ref={sectionRefs.performance} className="fade-section">
            Performance & QoR Tuning
          </h2>
          <p className="fade-section">
            <Highlight
              text="Optimizing an IP's quality of results (QoR) involves balancing latency, throughput, area, and power. Microarchitectural tweaks—such as deeper pipelining, or local on-chip buffering—can dramatically improve clock-frequency scalability."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Physical constraints (SDC) guide synthesis and placement: defining false paths, multi-cycle paths, and multi-voltage domains for power islands ensures the IP meets end-to-end timing budgets in the full chip context."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Co-optimizing the IP's RTL with floorplanning feedback (via incremental ECO flows) can unlock additional performance headroom in critical blocks."
              query={searchTerm}
            />
          </p>

          {/* 7. Security & Licensing */}
          <h2 ref={sectionRefs.security} className="fade-section">
            Security & Licensing
          </h2>
          <p className="fade-section">
            <Highlight
              text="Commercial IP often requires encryption and obfuscation to protect vendor investment. License managers enforce authorized use, while hardware root-of-trust designs prevent cloning or unauthorized instantiation."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Some IPs include runtime-configurable secure features, like AES key reload or PUF-based key generation, requiring additional test modes and secure boot integration in the SoC bootloader."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Open-source IPs use permissive or copyleft licenses (MIT, GPL), requiring clear documentation of usage obligations and potential patent considerations."
              query={searchTerm}
            />
          </p>

          {/* 8. Future Directions */}
          <h2 ref={sectionRefs.future} className="fade-section">
            Future Directions in IP Ecosystems
          </h2>
          <p className="fade-section">
            <Highlight
              text="The IP ecosystem is moving toward cloud-based catalogs, AI-driven IP selection tools, and collaborative platforms that track IP lineage, quality scores, and integration feedback in real time."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Machine-learning models will predict integration risk, automatically suggest parameter sweeps, and estimate power/performance trade-offs before RTL is even generated."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Standardized IP metadata schemas (IP-XACT 2021, IEEE 1801) increasingly support security tags, quality metrics, and configurability attributes—streamlining end-to-end design automation."
              query={searchTerm}
            />
          </p>

          {/* Comment Section */}
          <div className="comment-section" ref={sectionRefs.comments}>
            <h3>2 Comments</h3>

            {/* Comment 1 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">Alice Architect</span>
                    <span className="comment-date">March 10, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                Fantastic overview of IP types and integration flows—very insightful!
              </p>
              <button className="reply-button" onClick={() => toggleReply("alice")}>
                Reply
              </button>
              {replyVisible["alice"] && (
                <div className="leave-comment">
                  <h3>Reply to Alice Architect</h3>
                  <form onSubmit={handleReplySubmit}>
                    <div className="form-group">
                      <label>Name</label>
                      <input type="text" placeholder="Your name..." required />
                    </div>
                    <div className="form-group">
                      <label>Message</label>
                      <textarea placeholder="Your reply..." required />
                    </div>
                    <button type="submit" className="send-message-btn">
                      Reply
                    </button>
                  </form>
                </div>
              )}
            </div>

            {/* Comment 2 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">Bob Builder</span>
                    <span className="comment-date">March 12, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                The security and licensing section was particularly useful—thanks!
              </p>
              <button className="reply-button" onClick={() => toggleReply("bob")}>
                Reply
              </button>
              {replyVisible["bob"] && (
                <div className="leave-comment">
                  <h3>Reply to Bob Builder</h3>
                  <form onSubmit={handleReplySubmit}>
                    <div className="form-group">
                      <label>Name</label>
                      <input type="text" placeholder="Your name..." required />
                    </div>
                    <div className="form-group">
                      <label>Message</label>
                      <textarea placeholder="Your reply..." required />
                    </div>
                    <button type="submit" className="send-message-btn">
                      Reply
                    </button>
                  </form>
                </div>
              )}
            </div>

            {/* Leave a comment */}
            <div className="leave-comment fade-section">
              <h3>Leave a comment</h3>
              <p>Have questions or feedback? Fill out the form below!</p>
              <form onSubmit={handleCommentSubmit}>
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" placeholder="Name here..." required />
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea placeholder="Message here..." required />
                </div>
                <button type="submit" className="send-message-btn">
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Desktop Sidebar */}
        <div className="blog-right-column fade-section">
          <div className="search-box">
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              type="text"
              placeholder="Search keyword..."
              className="search-input"
            />
            <button onClick={handleSearch} className="search-button">
              Search
            </button>
          </div>
          <div className="table-of-contents">
            <h4>In This Article</h4>
            <ul>
              {Object.entries(sectionRefs).map(([key, ref]) => (
                <li key={key}>
                  <button onClick={() => scrollTo(ref)}>
                    {{
                      intro: "Introduction",
                      overview: "IP Overview & Classification",
                      integration: "Integration Flow",
                      customization: "Customization & Parameterization",
                      verification: "Verification & Validation",
                      performance: "Performance & QoR",
                      security: "Security & Licensing",
                      future: "Future Directions",
                      comments: "Comments",
                    }[key]}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Thank You Popup */}
        <AnimatePresence>
          {showPopup && (
            <motion.div
              className="thank-you-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeOverlay}
            >
              <motion.div
                className="thank-you-content"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                transition={{ type: "spring", stiffness: 300 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="checkmark-container">
                  <motion.svg
                    className="checkmark"
                    viewBox="0 0 52 52"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1, ease: "easeInOut" }}
                  >
                    <circle cx="26" cy="26" r="25" stroke="#007bff" strokeWidth="2" />
                    <path
                      d="M16 27.5L23 34.5L36 19.5"
                      stroke="#007bff"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </motion.svg>
                </div>
                <h3>Thank you for the feedback!</h3>
                <p>We appreciate your comment.</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default IPDevelopment;